import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import HeaderOne from '../Components/Header/HeaderOne';
import FooterOne from '../Components/Footer/FooterOne';
import ScrollToTop from '../Components/ScrollToTop';
import '../assets/css/hotel-styles.css';

function HotelDetails() {
    const { id } = useParams();
    const [hotel, setHotel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(0);

    useEffect(() => {
        const fetchHotelDetails = async () => {
            try {
                setLoading(true);
                
                // Try to fetch from your admin portal API
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/bnb/${id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                });
                
                if (response.ok) {
                    const data = await response.json();
                    // Map admin portal data to our hotel format
                    const mappedHotel = {
                        id: data.id,
                        name: data.name || data.title,
                        location: data.location || data.address,
                        rating: parseFloat(data.rating) || 4.0,
                        reviewCount: data.reviews_count || Math.floor(Math.random() * 1000) + 100,
                        price: parseFloat(data.price_per_night) || data.price,
                        originalPrice: data.original_price || (parseFloat(data.price_per_night) * 1.2),
                        images: data.images || [data.featured_image || '/assets/img/gallery/gallery_2_4.jpg'],
                        amenities: data.amenities ? data.amenities.split(',') : ["Free WiFi", "Breakfast"],
                        propertyType: data.property_type || "hotel",
                        featured: data.is_featured || false,
                        freeCancellation: data.free_cancellation || true,
                        description: data.description || "Comfortable accommodation",
                        fullDescription: data.full_description || data.description || "Comfortable accommodation with modern amenities.",
                        checkInTime: data.check_in_time || "3:00 PM",
                        checkOutTime: data.check_out_time || "11:00 AM",
                        cancellationPolicy: data.cancellation_policy || "Free cancellation up to 24 hours before check-in"
                    };
                    setHotel(mappedHotel);
                } else {
                    throw new Error('API response not ok');
                }
            } catch (error) {
                console.log('Using fallback hotel data - API not available:', error.message);
                // Fallback data for development
                const fallbackHotel = {
                    id: parseInt(id),
                    name: "Luxury Downtown Hotel",
                    location: "City Center, New York",
                    rating: 4.5,
                    reviewCount: 1250,
                    price: 189,
                    originalPrice: 220,
                    images: [
                        "/assets/img/gallery/gallery_12_1.jpg",
                        "/assets/img/gallery/gallery_8_8.jpg",
                        "/assets/img/gallery/gallery_7_9.jpg"
                    ],
                    amenities: ["Free WiFi", "Pool", "Gym", "Spa", "Restaurant", "Room Service", "Concierge", "Parking", "Business Center", "Laundry"],
                    propertyType: "hotel",
                    featured: true,
                    freeCancellation: true,
                    description: "Modern luxury hotel in the heart of the city with exceptional amenities",
                    fullDescription: "Experience luxury and comfort at our premier downtown hotel. Located in the heart of the city, we offer spacious rooms with modern amenities, world-class dining, and exceptional service. Perfect for both business and leisure travelers, our hotel features a full-service spa, fitness center, and rooftop pool with stunning city views.",
                    checkInTime: "3:00 PM",
                    checkOutTime: "11:00 AM",
                    cancellationPolicy: "Free cancellation up to 24 hours before check-in"
                };
                setHotel(fallbackHotel);
            } finally {
                setLoading(false);
            }
        };

        fetchHotelDetails();
    }, [id]);

    const renderStars = (rating) => {
        const stars = [];
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 !== 0;

        for (let i = 0; i < fullStars; i++) {
            stars.push(<i key={i} className="fas fa-star text-warning"></i>);
        }
        
        if (hasHalfStar) {
            stars.push(<i key="half" className="fas fa-star-half-alt text-warning"></i>);
        }
        
        const remainingStars = 5 - Math.ceil(rating);
        for (let i = 0; i < remainingStars; i++) {
            stars.push(<i key={`empty-${i}`} className="far fa-star text-warning"></i>);
        }
        
        return stars;
    };

    const handleBookNow = () => {
        // TODO: Implement booking functionality
        console.log('Booking hotel:', hotel);
        alert(`Booking ${hotel.name} - This would redirect to your booking system`);
    };

    if (loading) {
        return (
            <div>
                <HeaderOne />
                <div className="container py-5">
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading hotel details...</span>
                        </div>
                        <p className="mt-3">Loading hotel information...</p>
                    </div>
                </div>
                <FooterOne />
                <ScrollToTop />
            </div>
        );
    }

    if (!hotel) {
        return (
            <div>
                <HeaderOne />
                <div className="container py-5">
                    <div className="text-center">
                        <h2>Hotel not found</h2>
                        <p>The hotel you're looking for doesn't exist or has been removed.</p>
                        <a href="/home-bnb" className="btn btn-primary">Back to Hotels</a>
                    </div>
                </div>
                <FooterOne />
                <ScrollToTop />
            </div>
        );
    }

    return (
        <div>
            <HeaderOne />
            
            {/* Hotel Details Section */}
            <div className="hotel-details-area py-5">
                <div className="container">
                    {/* Hotel Header */}
                    <div className="row mb-4">
                        <div className="col-lg-8">
                            <h1 className="hotel-title">{hotel.name}</h1>
                            <p className="hotel-location mb-2">
                                <i className="fas fa-map-marker-alt text-primary me-2"></i>
                                {hotel.location}
                            </p>
                            <div className="hotel-rating mb-3">
                                <div className="stars me-2">
                                    {renderStars(hotel.rating)}
                                </div>
                                <span className="rating-text">
                                    {hotel.rating} ({hotel.reviewCount} reviews) • {hotel.propertyType}
                                </span>
                            </div>
                        </div>
                        <div className="col-lg-4 text-end">
                            <div className="price-display mb-3">
                                {hotel.originalPrice > hotel.price && (
                                    <div className="original-price text-muted">
                                        <s>${hotel.originalPrice}</s>
                                    </div>
                                )}
                                <div className="current-price h2">
                                    ${hotel.price}
                                    <span className="price-period h6 text-muted">/night</span>
                                </div>
                            </div>
                            <button className="btn btn-primary btn-lg px-4" onClick={handleBookNow}>
                                <i className="fas fa-calendar-check me-2"></i>
                                Book Now
                            </button>
                        </div>
                    </div>

                    {/* Image Gallery */}
                    <div className="row mb-5">
                        <div className="col-lg-8">
                            <div className="image-gallery">
                                <div className="main-image mb-3">
                                    <img 
                                        src={hotel.images[selectedImage] || hotel.images[0] || '/assets/img/gallery/gallery_2_4.jpg'}
                                        alt={hotel.name}
                                        className="img-fluid rounded"
                                        style={{ width: '100%', height: '400px', objectFit: 'cover' }}
                                    />
                                </div>
                                <div className="image-thumbnails">
                                    <div className="row g-2">
                                        {hotel.images.map((image, index) => (
                                            <div key={index} className="col-3">
                                                <img 
                                                    src={image}
                                                    alt={`${hotel.name} ${index + 1}`}
                                                    className={`img-fluid rounded cursor-pointer ${selectedImage === index ? 'border border-primary' : ''}`}
                                                    style={{ height: '80px', objectFit: 'cover', cursor: 'pointer' }}
                                                    onClick={() => setSelectedImage(index)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4">
                            <div className="booking-card card">
                                <div className="card-body">
                                    <h5 className="card-title">Booking Information</h5>
                                    <div className="booking-info">
                                        <div className="info-item mb-2">
                                            <strong>Check-in:</strong> {hotel.checkInTime}
                                        </div>
                                        <div className="info-item mb-2">
                                            <strong>Check-out:</strong> {hotel.checkOutTime}
                                        </div>
                                        <div className="info-item mb-3">
                                            <strong>Cancellation:</strong> {hotel.cancellationPolicy}
                                        </div>
                                        {hotel.freeCancellation && (
                                            <div className="alert alert-success">
                                                <i className="fas fa-check-circle me-2"></i>
                                                Free cancellation available
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Hotel Information */}
                    <div className="row">
                        <div className="col-lg-8">
                            {/* Description */}
                            <div className="section mb-5">
                                <h3 className="section-title">About this property</h3>
                                <p className="text-justify">{hotel.fullDescription}</p>
                            </div>

                            {/* Amenities */}
                            <div className="section mb-5">
                                <h3 className="section-title">Amenities</h3>
                                <div className="amenities-grid">
                                    <div className="row">
                                        {hotel.amenities.map((amenity, index) => (
                                            <div key={index} className="col-md-6 col-lg-4 mb-3">
                                                <div className="amenity-item d-flex align-items-center">
                                                    <i className="fas fa-check text-success me-2"></i>
                                                    <span>{amenity}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Location */}
                            <div className="section mb-5">
                                <h3 className="section-title">Location</h3>
                                <p className="mb-3">{hotel.location}</p>
                                <div className="map-placeholder bg-light rounded p-5 text-center">
                                    <i className="fas fa-map fa-3x text-muted mb-3"></i>
                                    <p className="text-muted">Interactive map would be displayed here</p>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-4">
                            {/* Similar Properties */}
                            <div className="section">
                                <h4>You might also like</h4>
                                <div className="similar-properties">
                                    {/* This would be populated with similar properties */}
                                    <div className="card mb-3">
                                        <div className="card-body">
                                            <h6 className="card-title">More hotels in {hotel.location.split(',')[0]}</h6>
                                            <p className="card-text small text-muted">
                                                Discover more accommodations in this area
                                            </p>
                                            <a href="/home-bnb" className="btn btn-outline-primary btn-sm">
                                                View All
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <FooterOne />
            <ScrollToTop />
        </div>
    );
}

export default HotelDetails;
